/**
 * Analytics Events — Catálogo tipado de eventos padronizados
 *
 * Eventos seguem especificações oficiais:
 * - GA4: https://developers.google.com/analytics/devguides/collection/ga4/reference/events
 * - Meta Pixel: https://developers.facebook.com/docs/meta-pixel/reference
 * - Google Ads: https://support.google.com/google-ads/answer/13258081
 *
 * Todos os eventos são "determinísticos" — a IA NÃO inventa eventos novos.
 * Se precisar de um novo evento, adicione aqui primeiro.
 */

/* ═══════════════════════════════════════════
   GA4 — Eventos Recomendados (Oficiais)
   ═══════════════════════════════════════════ */

export interface PageViewParams {
  page_path: string;
  page_title?: string;
  page_location?: string;
  language?: string;
}

export interface SearchParams {
  search_term: string;
  search_category?: string;
  results_count?: number;
}

export interface SelectContentParams {
  content_type: string;
  content_id: string;
}

export interface ViewItemParams {
  currency: string;
  value: number;
  items: ProductItem[];
}

export interface BeginCheckoutParams {
  currency: string;
  value: number;
  items: ProductItem[];
}

export interface PurchaseParams {
  transaction_id?: string;
  value: number;
  currency: string;
  items: ProductItem[];
  coupon?: string;
}

export interface GenerateLeadParams {
  currency?: string;
  value?: number;
}

export interface SignUpParams {
  method: string;
}

export interface ShareParams {
  method: string;
  content_type: string;
  item_id: string;
}

export interface VideoStartParams {
  video_title?: string;
  video_provider?: string;
  video_url?: string;
}

/* ═══════════════════════════════════════════
   E-commerce — Itens
   ═══════════════════════════════════════════ */

export interface ProductItem {
  item_id: string;
  item_name: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  price?: number;
  quantity?: number;
  item_brand?: string;
  item_variant?: string;
  affiliation?: string;
  currency?: string;
  discount?: number;
  index?: number;
}

/* ═══════════════════════════════════════════
   Eventos Customizados — Domínio do Site
   ═══════════════════════════════════════════ */

export interface WhatsAppClickParams {
  provider: string;
  source: string;
  entity_type?: string;
  entity_name?: string;
  entity_id?: string;
  destination?: string;
  affiliate?: boolean;
}

export interface BookingClickParams {
  provider: string;
  entity_type: string;
  entity_name: string;
  entity_id: string;
  price?: number;
  currency?: string;
  nights?: number;
  guests?: number;
  check_in?: string;
  check_out?: string;
}

export interface AffiliateClickParams {
  provider: string;
  platform: 'vai-de-promo' | 'booking' | 'outro';
  product_type: 'flight' | 'hotel' | 'insurance' | 'tour';
  origin?: string;
  destination?: string;
  dates?: string;
  value?: number;
  currency?: string;
}

export interface NewsletterSignupParams {
  source: string;
  page: string;
  language: string;
}

export interface LeadMagnetDownloadParams {
  resource: string;
  source: string;
  language: string;
}

export interface ComparisonViewParams {
  category: string;
  items_compared: string[];
  winner?: string;
}

export interface EntityViewParams {
  entity_type: string;
  entity_name: string;
  entity_id: string;
  category?: string;
  vilarejo?: string;
  partner?: boolean;
}

export interface ItineraryViewParams {
  itinerary_id: string;
  itinerary_name: string;
  days: number;
  language: string;
}

export interface WidgetInteractParams {
  widget_type:
    | 'weather'
    | 'flights'
    | 'map'
    | 'wikiloc'
    | 'booking'
    | 'passes'
    | 'mare'
    | 'feriados';
  action: 'open' | 'search' | 'filter' | 'click' | 'close';
  search_term?: string;
  result_count?: number;
}

export interface OutboundClickParams {
  url: string;
  label: string;
  category: string;
  external_domain: string;
}

export interface ScrollDepthParams {
  depth_percent: number;
  page_path: string;
}

export interface TimeOnPageParams {
  seconds: number;
  page_path: string;
}

export interface AppErrorParams {
  error_name: string;
  component_stack: string;
  page_path: string;
}

export interface QualifyLeadParams {
  lead_type: 'whatsapp_click' | 'newsletter_signup' | 'leadmagnet_download' | 'booking_click';
  page_path: string;
  source?: string;
}

/* ═══════════════════════════════════════════
   Union Type — Todos os Eventos
   ═══════════════════════════════════════════ */

export type AnalyticsEvent =
  | { name: 'page_view'; params: PageViewParams }
  | { name: 'search'; params: SearchParams }
  | { name: 'select_content'; params: SelectContentParams }
  | { name: 'view_item'; params: ViewItemParams }
  | { name: 'begin_checkout'; params: BeginCheckoutParams }
  | { name: 'purchase'; params: PurchaseParams }
  | { name: 'generate_lead'; params: GenerateLeadParams }
  | { name: 'sign_up'; params: SignUpParams }
  | { name: 'share'; params: ShareParams }
  | { name: 'video_start'; params: VideoStartParams }
  | { name: 'whatsapp_click'; params: WhatsAppClickParams }
  | { name: 'booking_click'; params: BookingClickParams }
  | { name: 'affiliate_click'; params: AffiliateClickParams }
  | { name: 'newsletter_signup'; params: NewsletterSignupParams }
  | { name: 'leadmagnet_download'; params: LeadMagnetDownloadParams }
  | { name: 'comparison_view'; params: ComparisonViewParams }
  | { name: 'entity_view'; params: EntityViewParams }
  | { name: 'itinerary_view'; params: ItineraryViewParams }
  | { name: 'widget_interact'; params: WidgetInteractParams }
  | { name: 'outbound_click'; params: OutboundClickParams }
  | { name: 'scroll_depth'; params: ScrollDepthParams }
  | { name: 'time_on_page'; params: TimeOnPageParams }
  | { name: 'app_error'; params: AppErrorParams }
  | { name: 'qualify_lead'; params: QualifyLeadParams };

/* ═══════════════════════════════════════════
   Helpers — Criadores de Eventos
   ═══════════════════════════════════════════ */

export const Events = {
  pageView: (path: string, title?: string, lang?: string): AnalyticsEvent => ({
    name: 'page_view',
    params: {
      page_path: path,
      page_title: title,
      page_location: typeof window !== 'undefined' ? window.location.href : undefined,
      language: lang,
    },
  }),

  search: (term: string, category?: string, results?: number): AnalyticsEvent => ({
    name: 'search',
    params: { search_term: term, search_category: category, results_count: results },
  }),

  whatsappClick: (p: WhatsAppClickParams): AnalyticsEvent => ({
    name: 'whatsapp_click',
    params: p,
  }),

  bookingClick: (p: BookingClickParams): AnalyticsEvent => ({
    name: 'booking_click',
    params: p,
  }),

  affiliateClick: (p: AffiliateClickParams): AnalyticsEvent => ({
    name: 'affiliate_click',
    params: p,
  }),

  newsletterSignup: (source: string, page: string, language: string): AnalyticsEvent => ({
    name: 'newsletter_signup',
    params: { source, page, language },
  }),

  leadMagnetDownload: (resource: string, source: string, language: string): AnalyticsEvent => ({
    name: 'leadmagnet_download',
    params: { resource, source, language },
  }),

  entityView: (p: EntityViewParams): AnalyticsEvent => ({
    name: 'entity_view',
    params: p,
  }),

  itineraryView: (id: string, name: string, days: number, language: string): AnalyticsEvent => ({
    name: 'itinerary_view',
    params: { itinerary_id: id, itinerary_name: name, days, language },
  }),

  widgetInteract: (p: WidgetInteractParams): AnalyticsEvent => ({
    name: 'widget_interact',
    params: p,
  }),

  outboundClick: (url: string, label: string, category: string): AnalyticsEvent => ({
    name: 'outbound_click',
    params: { url, label, category, external_domain: new URL(url).hostname },
  }),

  scrollDepth: (depth: number, path: string): AnalyticsEvent => ({
    name: 'scroll_depth',
    params: { depth_percent: depth, page_path: path },
  }),

  timeOnPage: (seconds: number, path: string): AnalyticsEvent => ({
    name: 'time_on_page',
    params: { seconds, page_path: path },
  }),

  share: (method: string, contentType: string, itemId: string): AnalyticsEvent => ({
    name: 'share',
    params: { method, content_type: contentType, item_id: itemId },
  }),

  videoStart: (title: string, provider: string, url: string): AnalyticsEvent => ({
    name: 'video_start',
    params: { video_title: title, video_provider: provider, video_url: url },
  }),

  generateLead: (value?: number, currency?: string): AnalyticsEvent => ({
    name: 'generate_lead',
    params: { value, currency },
  }),

  signUp: (method: string): AnalyticsEvent => ({
    name: 'sign_up',
    params: { method },
  }),

  purchase: (p: PurchaseParams): AnalyticsEvent => ({
    name: 'purchase',
    params: p,
  }),

  appError: (errorName: string, stack: string, path: string): AnalyticsEvent => ({
    name: 'app_error',
    params: { error_name: errorName, component_stack: stack, page_path: path },
  }),

  qualifyLead: (
    leadType: QualifyLeadParams['lead_type'],
    source?: string,
    path?: string
  ): AnalyticsEvent => ({
    name: 'qualify_lead',
    params: {
      lead_type: leadType,
      page_path: path || (typeof window !== 'undefined' ? window.location.pathname : ''),
      source,
    },
  }),
} as const;
